import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState, type ReactNode } from "react";
import {
    DatePicker as ZhDatePicker,
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { APP_VERSION_TYPE } from "@zhihaoscm/meta";
import RequiredMark from "@/views/components/RequiredMark";
import {
    AppVersionUsageDialog,
    exportAppVersionUsage,
    loadAppVersionUsage,
    type AppVersionUsageQuery,
    type AppVersionUsageRow
} from "./AppVersionUsageDialog";

export interface AppVersionRow {
    id: number;
    type?: number;
    version?: string;
    publishDate?: string | Record<string, unknown>;
    ios?: number;
    android?: number;
    isForce?: number;
    forceUpgrade?: boolean;
    updateTitle?: string;
    updateLog?: string;
    installPackageFileId?: number;
}

export interface AppVersionQuery {
    page: number;
    size: number;
    type: number;
    beginTime?: string;
    endTime?: string;
}

export interface AppVersionFormValues {
    type: number;
    version: string;
    publishDate: string;
    installPackageFileId?: number;
    forceUpgrade: boolean;
    updateTitle: string;
    updateLog: string;
}

export interface AppVersionPageProps {
    appType?: number;
    loadVersions?: (query: AppVersionQuery) => Promise<{ list?: AppVersionRow[]; total?: number }>;
    loadDetail?: (id: number) => Promise<AppVersionRow>;
    loadInstallPackageFile?: (id: number) => Promise<{ id: number; name: string; path: string }>;
    create?: (values: {
        type: number;
        version: string;
        publishDate: string;
        installPackageFileId: number;
        focus: number;
        title: string;
        log: string;
    }) => Promise<unknown>;
    update?: (id: number, values: { title: string; log: string }) => Promise<unknown>;
    remove?: (id: number, type: number) => Promise<unknown>;
    loadUsage?: (query: AppVersionUsageQuery) => Promise<{ list?: AppVersionUsageRow[]; total?: number }>;
    exportUsage?: (query: AppVersionUsageQuery, filename: string) => Promise<unknown>;
}

function getCurrentDateTime(): string {
    const current = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    // Vue 使用本地时间初始化发布日期，避免以 UTC 日期造成跨日偏差。
    return `${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}T${pad(current.getHours())}:${pad(current.getMinutes())}:${pad(current.getSeconds())}`;
}

/** 按 Vue 版本号输入规则仅保留数字/点，并限制为最多三段。 */
function sanitizeVersionInput(value: string): string {
    const filtered = value.replace(/[^\d.]/g, "");
    const parts = filtered.split(".");
    return parts.length > 3 ? parts.slice(0, 3).join(".") : filtered;
}

// 旧 Vue 接口契约默认每页展示 20 条，列表切换类型或重置筛选时保持这一分页基线。
const defaultQuery = (type: number): AppVersionQuery => ({ page: 1, size: 20, type });
const defaultForm = (type: number): AppVersionFormValues => ({
    type,
    version: "",
    publishDate: getCurrentDateTime(),
    installPackageFileId: undefined,
    forceUpgrade: false,
    updateTitle: "发现新版本",
    updateLog: "您当前使用的“链云砂石”APP不是最新版，请下载最新版本，体验更多功能."
});

const APP_PACKAGE_MAX_SIZE = 1000 * 1024 * 1024;

/** 读取旧 Vue 跳转暂存的 APP 类型；显式传入类型时优先使用测试或路由参数。 */
export function resolveAppVersionType(appType?: number): number {
    if (appType != null) return appType;
    const storedType = Number(localStorage.getItem("appVersion"));
    return storedType === APP_VERSION_TYPE.SHIP.code ? storedType : APP_VERSION_TYPE.LIAN_YUN.code;
}

/** 复用 Vue APP 版本分页接口。 */
export async function loadAppVersions(query: AppVersionQuery): Promise<{ list?: AppVersionRow[]; total?: number }> {
    const response = await request.service.get("/admin/app-version/paging", { params: query });
    return response.data;
}

/** 复用 Vue APP 版本详情接口。 */
export async function loadAppVersionDetail(id: number): Promise<AppVersionRow> {
    const response = await request.service.get(`/admin/app-version/${id}`);
    return response.data;
}

/** 复用 Vue single-upload 的文件回显接口，编辑模式仅加载文件元数据供查看/下载。 */
export async function loadAppVersionInstallPackageFile(
    id: number
): Promise<{ id: number; name: string; path: string }> {
    const { fileService } = await import("@/api/services/fileService");
    const file = await fileService.getFileById(id);
    return { id: Number(file.id), name: file.name, path: file.path };
}

/** 复用 Vue APP 版本新增接口。 */
export async function createAppVersion(values: {
    type: number;
    version: string;
    publishDate: string;
    installPackageFileId: number;
    focus: number;
    title: string;
    log: string;
}): Promise<unknown> {
    const response = await request.service.post("/admin/app-version", values);
    return response.data;
}

/** 复用 Vue APP 版本编辑接口。 */
export async function updateAppVersion(id: number, values: { title: string; log: string }): Promise<unknown> {
    const response = await request.service.put(`/admin/app-version/${id}`, values);
    return response.data;
}

/** 复用 Vue APP 版本删除接口。 */
export async function deleteAppVersion(id: number, type: number): Promise<unknown> {
    const response = await request.service.delete(`/admin/app-version/${id}?type=${type}`);
    return response.data;
}

function formatDate(value?: string | Record<string, unknown>): string {
    if (!value) return DEFAULT_VALUE;
    if (typeof value === "object" && value.year && value.month && value.day) {
        return `${value.year}-${String(value.month).padStart(2, "0")}-${String(value.day).padStart(2, "0")}`;
    }
    return String(value).replace("T", " ").slice(0, 16);
}

/** 按浏览器本地日历格式化日期，避免 UTC 转换造成跨日误差。 */
export function toLocalDateValue(value: Date): string {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

/** 对齐旧 Vue：发布日期只能选择当前本地日期及之前的日期。 */
export function isAppVersionPublishDateDisabled(value: Date, now = new Date()): boolean {
    return toLocalDateValue(value) > toLocalDateValue(now);
}

function AppVersionEditor({
    appType,
    id,
    open,
    loadDetail,
    loadInstallPackageFile,
    create,
    update,
    onClose,
    onSaved
}: {
    appType: number;
    id?: number;
    open: boolean;
    loadDetail: (id: number) => Promise<AppVersionRow>;
    loadInstallPackageFile: (id: number) => Promise<{ id: number; name: string; path: string }>;
    create: AppVersionPageProps["create"];
    update: AppVersionPageProps["update"];
    onClose: () => void;
    onSaved: () => void;
}) {
    const [form, setForm] = useState<AppVersionFormValues>(() => defaultForm(appType));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [packageFile, setPackageFile] = useState<{ id: number; name: string; path: string } | null>(null);
    const [packageFileList, setPackageFileList] = useState<UploadUserFile[]>([]);
    useEffect(() => {
        if (!open) return;
        setErrors({});
        setErrorMessage(null);
        setPackageFile(null);
        setPackageFileList([]);
        if (id == null) {
            setForm(defaultForm(appType));
            return;
        }
        void loadDetail(id)
            .then(async detail => {
                setForm({
                    type: appType,
                    version: detail.version || "",
                    publishDate:
                        typeof detail.publishDate === "string" ? detail.publishDate : formatDate(detail.publishDate),
                    installPackageFileId: detail.installPackageFileId,
                    forceUpgrade: detail.forceUpgrade ?? detail.isForce === 1,
                    updateTitle: detail.updateTitle || "",
                    updateLog: detail.updateLog || ""
                });
                if (detail.installPackageFileId) {
                    try {
                        // Vue single-upload 在有文件 ID 但没有文件实体时会补查文件详情。
                        setPackageFile(await loadInstallPackageFile(detail.installPackageFileId));
                    } catch {
                        setErrorMessage("安装包信息加载失败");
                    }
                }
            })
            .catch(() => setErrorMessage("APP版本详情加载失败"));
    }, [appType, id, loadDetail, loadInstallPackageFile, open]);

    const updateForm = <K extends keyof AppVersionFormValues>(key: K, value: AppVersionFormValues[K]) => {
        setForm(current => ({ ...current, [key]: value }));
        setErrors(current => ({ ...current, [String(key)]: "" }));
    };

    /** 复用 Vue single-upload 的 APK 类型和 1000MB 限制。 */
    const validateInstallPackage = (file: File) => {
        if (id != null) return false;
        if (!file.name.toLowerCase().endsWith(".apk")) {
            setErrorMessage("仅支持 APK 文件");
            return false;
        }
        if (file.size > APP_PACKAGE_MAX_SIZE) {
            setErrorMessage("安装包大小不能超过1000MB");
            return false;
        }
        return true;
    };

    /** 使用 zhihao Upload 自定义请求，上传成功后回填安装包文件 ID。 */
    const uploadInstallPackage = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            // 文件服务只在发布者选择安装包时加载，避免版本列表和编辑弹窗被上传模块阻塞。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
            updateForm("installPackageFileId", fileId);
            setPackageFile({ id: fileId, name: file.name, path: "" });
            setPackageFileList([{ uid: fileId, name: file.name, status: "success" }]);
            setErrorMessage(null);
            onSuccess(uploadedFile);
        } catch {
            setErrorMessage("安装包上传失败");
            onError(new Error("安装包上传失败"));
        }
    };

    const validate = () => {
        const nextErrors: Record<string, string> = {};
        if (id == null && !/^([1-9][0-9]?)\.[0-9]{1,2}\.[0-9]{1,2}$/.test(form.version)) {
            nextErrors.version = form.version ? "版本号格式不符合要求" : "请输入版本号";
        }
        if (id == null && !form.publishDate) nextErrors.publishDate = "请选择发布日期";
        if (id == null && !form.installPackageFileId) nextErrors.installPackageFileId = "请上传";
        if (!form.updateTitle.trim()) nextErrors.updateTitle = "请输入更新标题";
        if (!form.updateLog.trim()) nextErrors.updateLog = "请输入更新文案";
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        try {
            if (id != null) {
                await update?.(id, { title: form.updateTitle.trim(), log: form.updateLog.trim() });
            } else {
                await create?.({
                    type: appType,
                    version: form.version,
                    publishDate: form.publishDate,
                    installPackageFileId: form.installPackageFileId as number,
                    focus: form.forceUpgrade ? 1 : 0,
                    title: form.updateTitle.trim(),
                    log: form.updateLog.trim()
                });
            }
            onSaved();
            onClose();
        } catch {
            setErrorMessage("APP版本保存失败");
        }
    };

    // 旧 Vue el-form 按固定标签列和自适应控件列排布，错误文案与控件保持同列。
    const renderFormRow = (label: ReactNode, control: ReactNode, controlId?: string, error?: string) => (
        <div className="app-version-editor-form__row grid grid-cols-[67px_minmax(0,1fr)] items-start gap-3">
            {controlId ? (
                <label className="app-version-editor-form__label pt-1 text-right" htmlFor={controlId}>
                    {label}
                </label>
            ) : (
                <div className="app-version-editor-form__label pt-1 text-right">{label}</div>
            )}
            <div className="app-version-editor-form__control min-w-0">
                {control}
                {error ? (
                    <span className="mt-1 block text-red-500" role="alert">
                        {error}
                    </span>
                ) : null}
            </div>
        </div>
    );

    return (
        <ZhDialog
            alignCenter
            open={open}
            title={id == null ? "发布新版本" : "修改版本"}
            width={520}
            footer={null}
            onCancel={onClose}
        >
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            <div className="app-version-editor-form grid grid-cols-1 gap-5 pb-16">
                {renderFormRow(
                    <>
                        <RequiredMark />
                        版本号
                    </>,
                    <Input
                        aria-label="版本号"
                        id="app-version"
                        className="w-full"
                        maxLength={13}
                        placeholder="请输入.请输入.请输入"
                        disabled={id != null}
                        value={form.version}
                        onChange={event => updateForm("version", sanitizeVersionInput(event.target.value))}
                    />,
                    "app-version",
                    errors.version
                )}
                {renderFormRow(
                    <>
                        <RequiredMark />
                        发布日期
                    </>,
                    <ZhDatePicker
                        aria-label="发布日期"
                        id="app-version-publish-date"
                        className="w-full"
                        style={{ width: "100%" }}
                        disabled={id != null}
                        disabledDate={date => isAppVersionPublishDateDisabled(date)}
                        format="YYYY-MM-DD"
                        type="date"
                        value={form.publishDate}
                        valueFormat="YYYY-MM-DDTHH:mm:ss"
                        onChange={(_value, formattedValue) =>
                            updateForm("publishDate", typeof formattedValue === "string" ? formattedValue : "")
                        }
                    />,
                    "app-version-publish-date",
                    errors.publishDate
                )}
                {renderFormRow(
                    <>
                        <RequiredMark />
                        安装包
                    </>,
                    <>
                        {/* 保留旧测试与 payload 使用的文件 ID 字段，但不在 Vue 表单中额外占用一行。 */}
                        <input
                            aria-label="安装包ID"
                            className="sr-only"
                            disabled={id != null}
                            type="number"
                            value={form.installPackageFileId ?? ""}
                            onChange={event =>
                                updateForm("installPackageFileId", Number(event.target.value) || undefined)
                            }
                        />
                        {id != null ? (
                            packageFile ? (
                                <Space>
                                    <span>{packageFile.name}</span>
                                    <Button
                                        aria-label="查看/下载 安装包"
                                        type="link"
                                        onClick={() => window.open(packageFile.path, "_blank")}
                                    >
                                        查看/下载
                                    </Button>
                                </Space>
                            ) : (
                                <span>安装包加载中...</span>
                            )
                        ) : (
                            <ZhUpload
                                action=""
                                accept=".apk,application/vnd.android.package-archive"
                                fileList={packageFileList}
                                limit={1}
                                name="安装包文件"
                                beforeUpload={validateInstallPackage}
                                httpRequest={uploadInstallPackage}
                                onRemove={() => {
                                    updateForm("installPackageFileId", undefined);
                                    setPackageFile(null);
                                    setPackageFileList([]);
                                }}
                            >
                                <Button htmlType="button" type="link">
                                    上传安装包
                                </Button>
                            </ZhUpload>
                        )}
                    </>
                )}
                {renderFormRow(
                    <>
                        <RequiredMark />
                        强制升级
                    </>,
                    <fieldset className="flex min-w-0 items-center gap-4 border-0 p-0">
                        <legend className="sr-only">强制升级</legend>
                        <label className="inline-flex items-center gap-1">
                            <input
                                aria-label="强制升级-是"
                                checked={form.forceUpgrade}
                                disabled={id != null}
                                type="radio"
                                onChange={() => updateForm("forceUpgrade", true)}
                            />
                            是
                        </label>
                        <label className="inline-flex items-center gap-1">
                            <input
                                aria-label="强制升级-否"
                                checked={!form.forceUpgrade}
                                disabled={id != null}
                                type="radio"
                                onChange={() => updateForm("forceUpgrade", false)}
                            />
                            否
                        </label>
                    </fieldset>,
                    "app-version-force-upgrade"
                )}
                {renderFormRow(
                    <>
                        <RequiredMark />
                        更新标题
                    </>,
                    <Input
                        aria-label="更新标题"
                        id="app-version-title"
                        className="w-full"
                        maxLength={20}
                        placeholder="请输入更新标题"
                        showCount
                        value={form.updateTitle}
                        onChange={event => updateForm("updateTitle", event.target.value)}
                    />,
                    "app-version-title",
                    errors.updateTitle
                )}
                {renderFormRow(
                    <>
                        <RequiredMark />
                        更新文案
                    </>,
                    <Input.TextArea
                        aria-label="更新文案"
                        id="app-version-log"
                        className="w-full"
                        maxLength={100}
                        placeholder="请输入更新文案"
                        rows={4}
                        showCount
                        value={form.updateLog}
                        onChange={event => updateForm("updateLog", event.target.value)}
                    />,
                    "app-version-log",
                    errors.updateLog
                )}
                <div className="app-version-editor-form__actions col-start-1 flex justify-end">
                    <Space>
                        <Button className="w-[70px]" onClick={onClose}>
                            取消
                        </Button>
                        <Button aria-label="确认版本" className="w-[70px]" type="primary" onClick={() => void submit()}>
                            确认
                        </Button>
                    </Space>
                </div>
            </div>
        </ZhDialog>
    );
}

/** APP 版本列表与发布/编辑弹窗，保留 Vue 用户数删除边界和表单 payload。 */
export function AppVersionPage({
    appType: appTypeProp,
    loadVersions = loadAppVersions,
    loadDetail = loadAppVersionDetail,
    loadInstallPackageFile = loadAppVersionInstallPackageFile,
    create = createAppVersion,
    update = updateAppVersion,
    remove = deleteAppVersion,
    loadUsage = loadAppVersionUsage,
    exportUsage = exportAppVersionUsage
}: AppVersionPageProps) {
    const [appType] = useState(() => resolveAppVersionType(appTypeProp));
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<AppVersionRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState(() => defaultQuery(appType));
    const [publishDateRange, setPublishDateRange] = useState({ beginDate: "", endDate: "" });
    const [editor, setEditor] = useState<{ open: boolean; id?: number }>({ open: false });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [usage, setUsage] = useState<{ open: boolean; id?: number; platform: "ios" | "android" }>({
        open: false,
        platform: "ios"
    });

    useEffect(() => {
        if (appTypeProp == null) localStorage.removeItem("appVersion");
    }, [appTypeProp]);

    useEffect(() => {
        let disposed = false;
        void loadVersions({ ...query, type: appType })
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("APP版本列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [appType, loadVersions, query, reloadToken]);

    const deleteRow = async (row: AppVersionRow) => {
        if ((row.ios || 0) > 0 || (row.android || 0) > 0) {
            setErrorMessage("该版本已有用户，不可删除！");
            return;
        }
        try {
            await remove(row.id, appType);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("APP版本删除失败");
        }
    };

    const updatePublishDateRange = (formattedValue: [string, string] | null) => {
        const [beginDate = "", endDate = ""] = formattedValue || [];
        const nextRange = { beginDate, endDate };
        setPublishDateRange(nextRange);
        setQuery(current => {
            const nextQuery = { ...current, page: 1 };
            // Vue 日期范围选择完成后将开始日和结束日转换为当天起止时间。
            if (nextRange.beginDate && nextRange.endDate) {
                nextQuery.beginTime = `${nextRange.beginDate}T00:00:00`;
                nextQuery.endTime = `${nextRange.endDate}T23:59:59`;
            } else {
                delete nextQuery.beginTime;
                delete nextQuery.endTime;
            }
            return nextQuery;
        });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue APP 版本列表的用户数、状态和操作插槽。
    const columns: TableColumnProps<AppVersionRow>[] = [
        { key: "id", label: "编号", prop: "id", render: ({ row }) => row.id },
        { key: "version", label: "版本号", prop: "version", render: ({ row }) => row.version || DEFAULT_VALUE },
        {
            key: "publishDate",
            label: "发布日期",
            prop: "publishDate",
            render: ({ row }) => formatDate(row.publishDate)
        },
        {
            key: "ios",
            label: "IOS用户数",
            prop: "ios",
            render: ({ row }) =>
                (row.ios || 0) > 0 ? (
                    <Button
                        aria-label={`IOS用户数 ${row.id}`}
                        type="link"
                        onClick={() => setUsage({ open: true, id: row.id, platform: "ios" })}
                    >
                        {row.ios}
                    </Button>
                ) : (
                    (row.ios ?? DEFAULT_VALUE)
                )
        },
        {
            key: "android",
            label: "Android用户数",
            prop: "android",
            render: ({ row }) =>
                (row.android || 0) > 0 ? (
                    <Button
                        aria-label={`Android用户数 ${row.id}`}
                        type="link"
                        onClick={() => setUsage({ open: true, id: row.id, platform: "android" })}
                    >
                        {row.android}
                    </Button>
                ) : (
                    (row.android ?? DEFAULT_VALUE)
                )
        },
        {
            key: "isForce",
            label: "是否强制更新",
            prop: "isForce",
            render: ({ row }) => <Tag>{Number(row.isForce) === 1 ? "是" : "否"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            prop: "id",
            render: ({ row }) => (
                <Space>
                    <Button
                        aria-label={`修改版本 ${row.id}`}
                        disabled={banButton}
                        type="link"
                        onClick={() => setEditor({ open: true, id: row.id })}
                    >
                        修改
                    </Button>
                    <Button
                        aria-label={`删除版本 ${row.id}`}
                        disabled={banButton}
                        type="link"
                        onClick={() => void deleteRow(row)}
                    >
                        删除
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <section aria-label="app-version-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<AppVersionRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-2 mt-0" level={4}>
                                APP版本管理
                            </Typography.Title>
                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <div className="app-version-list-toolbar mb-4 flex flex-wrap items-center gap-3">
                                <Button
                                    aria-label="发布新版本"
                                    type="primary"
                                    onClick={() => setEditor({ open: true })}
                                >
                                    发布新版本
                                </Button>
                                <ZhDateRangePicker
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="发布开始日期"
                                    type="daterange"
                                    value={[publishDateRange.beginDate || null, publishDateRange.endDate || null]}
                                    valueFormat="YYYY-MM-DD"
                                    endPlaceholder="发布结束日期"
                                    onChange={(_value, formattedValue) => updatePublishDateRange(formattedValue)}
                                />
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                alwaysShowQuickJumper
                                hideOnSinglePage
                                showSizeChanger
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 切换每页条数时沿用 Vue ProTable 契约，从第 1 页重新查询。
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <AppVersionUsageDialog
                appVersion={usage.id == null ? undefined : { id: usage.id, type: appType }}
                exportUsage={exportUsage}
                loadUsage={loadUsage}
                onClose={() => setUsage(current => ({ ...current, open: false }))}
                open={usage.open}
                platform={usage.platform}
            />
            <AppVersionEditor
                appType={appType}
                create={create}
                id={editor.id}
                loadDetail={loadDetail}
                loadInstallPackageFile={loadInstallPackageFile}
                open={editor.open}
                update={update}
                onClose={() => setEditor({ open: false })}
                onSaved={() => setReloadToken(value => value + 1)}
            />
        </section>
    );
}
